import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLoginModal } from "../components/LoginModalProvider";
import { useNavigate } from "react-router-dom";
import {
  Bell, MessageSquare, ChevronDown, MapPin, Calendar,
  HelpCircle, RefreshCw, Wallet, BookOpen, User, LogOut,
  AlertTriangle, MessageCircle, Flag, Info, Star as StarIcon,
  Home as HomeIcon, CalendarDays, Camera,
} from "lucide-react";
import AppLogo from "../components/AppLogo";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";
import { useBreakpoint } from "../hooks/useBreakpoint";

/* ═══════════════════════════════════════════════════════════
   DESIGN TOKENS — matched to the same values used across
   JiraniProDashboard.jsx and NotificationsPage.jsx, so this page
   is visually part of the same family rather than its own thing.
═══════════════════════════════════════════════════════════ */
const T = {
  green:       "#1e9254",
  greenDark:   "#155c36",
  greenMid:    "#1b7a47",
  greenLight:  "#2ab76b",
  greenDeep:   "#0d2e1a",
  greenTint:   "#eef8f3",
  greenBorder: "#a7d9bc",
  gold:        "#c9932c",
  goldLight:   "#e8c374",
  goldTint:    "#fbf3e3",
  purple:      "#7c5cff",

  bg:          "#F7F8FA",
  white:       "#ffffff",
  card:        "#ffffff",
  card2:       "#f7f8fa",
  border:      "#E5E7EB",
  borderFaint: "#F0F2F5",

  text:        "#0f1117",
  textMid:     "#2d3549",
  sub:         "#4a5268",
  faint:       "#8a93a8",
  ghost:       "#b0b8cc",

  hoverBg:     "#F3F4F6",

  amber:       "#b5780a",
  amberTint:   "#fdf6ea",
  amberBd:     "#eddab0",
  blue:        "#1d4ed8",
  blueTint:    "#eef3fd",
  blueBd:      "#c7d7f7",
  red:         "#c0392b",
  redTint:     "#fdf1ef",
  redBd:       "#f3cdc6",

  serif: "'Instrument Serif', Georgia, serif",
  sans:  "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",

  r8: "8px", r10: "10px", r12: "12px", r14: "14px", r16: "16px", rFull: "999px",
  s1: "0 1px 3px rgba(13,46,26,0.05), 0 1px 2px rgba(13,46,26,0.03)",
  s2: "0 4px 16px rgba(13,46,26,0.07), 0 1px 4px rgba(13,46,26,0.04)",
  s3: "0 8px 32px rgba(13,46,26,0.09), 0 2px 8px rgba(13,46,26,0.05)",
  sGreen: "0 4px 20px rgba(30,146,84,0.24)",
  tx: "all 0.18s cubic-bezier(.4,0,.2,1)",
};

if (typeof document !== "undefined" && !document.getElementById("__jpd_fonts")) {
  const l = document.createElement("link");
  l.id = "__jpd_fonts";
  l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap";
  document.head.appendChild(l);
}

const CATEGORY_LABELS = ["Quality of work", "Communication", "Punctuality", "Professionalism", "Value for money"];
const FILTERS = ["All", "5 star", "4 star", "3 star", "2 star", "1 star", "With photos", "Needs reply"];

const AVA_COLORS = ["#1e9254", "#155c36", "#374151", "#0f1117", "#b5780a", "#0d2e1a"];
const avaColor = (name = "") => AVA_COLORS[Math.abs((name.charCodeAt(0) || 0) + (name.charCodeAt(1) || 0)) % AVA_COLORS.length];
const timeAgo = (iso) => {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 3600000) return `${Math.max(1, Math.floor(diff / 60000))} min ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)} hr ago`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
  return new Date(iso).toLocaleDateString("en-KE", { day: "numeric", month: "short", year: "numeric" });
};

/* ═══════════════════════════════════════════════════════════
   SEED DATA — shown only in guest/preview mode (no session),
   clearly labelled via DemoBanner so it's never mistaken for a
   real professional's reviews.
═══════════════════════════════════════════════════════════ */
const SEED_PRO = {
  id: "pro_001", name: "James Mutua", firstName: "James", avatar_url: null, initials: "JM",
  rating: 4.8, reviewCount: 2,
};
const SEED_REVIEWS = [
  {
    id: "srv1", rating: 5,
    categoryRatings: { "Quality of work": 5, "Communication": 5, "Punctuality": 5, "Professionalism": 5, "Value for money": 4 },
    text: "James was incredibly thorough and professional. My apartment has never been this clean! He even organized my pantry without being asked.",
    photos: [], proReply: null, proReplyAt: null, moderationStatus: "approved",
    createdAt: new Date(Date.now() - 2 * 86400000).toISOString(),
    customer: { id: "c1", name: "Lucy Wanjiru", initials: "LW", avatar_url: null },
    service: "House Cleaning", location: "Karen, Nairobi", verified: true,
  },
  {
    id: "srv2", rating: 4,
    categoryRatings: { "Quality of work": 4, "Communication": 5, "Punctuality": 4, "Professionalism": 5, "Value for money": 4 },
    text: "On time, efficient, and did a fantastic job overall. Missed a couple of spots on the window sills but otherwise great.",
    photos: [], proReply: "Thank you Peter! I'll make sure to double-check window sills next time.", proReplyAt: new Date(Date.now() - 86400000).toISOString(),
    moderationStatus: "approved", createdAt: new Date(Date.now() - 5 * 86400000).toISOString(),
    customer: { id: "c2", name: "Peter Otieno", initials: "PO", avatar_url: null },
    service: "Deep Cleaning", location: "Westlands, Nairobi", verified: true,
  },
];
const SEED_NOTIFICATIONS = [
  { id: "n1", type: "review", title: "New review received", body: "Lucy Wanjiru left you a 5-star review", time: "2d ago", read: false },
];
const SEED_MESSAGES = [
  { id: "conv_001", customer: { id: "c1", name: "Lucy Wanjiru", initials: "LW", avatar_url: null }, lastMessage: "Thank you so much!", unread: 1, time: "1 hr ago" },
];

/* ── Supabase helpers ── */
async function fetchProfilesByIds(ids) {
  const uniqueIds = [...new Set((ids || []).filter(Boolean))];
  if (!uniqueIds.length) return {};
  const { data, error } = await supabase.from("profiles").select("id, full_name, avatar_url").in("id", uniqueIds);
  if (error) throw error;
  const map = {};
  (data || []).forEach(p => { map[p.id] = p; });
  return map;
}

async function fetchProProfile(userId) {
  const { data, error } = await supabase
    .from("profiles")
    .select("id, full_name, avatar_url, average_rating, review_count")
    .eq("id", userId).single();
  if (error) throw error;
  const name = data.full_name || "Pro";
  return {
    id: data.id, name, firstName: name.split(" ")[0],
    initials: name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase(),
    avatar_url: data.avatar_url, rating: data.average_rating || 0, reviewCount: data.review_count || 0,
  };
}

async function fetchReviewsFull(proId) {
  const { data, error } = await supabase
    .from("reviews")
    .select("id, rating, category_ratings, review_text, photos, pro_reply, pro_reply_at, moderation_status, created_at, customer_id, job_request_id")
    .eq("pro_id", proId)
    .order("created_at", { ascending: false })
    .limit(200);
  if (error) throw error;
  const rows = data || [];
  const profileMap = await fetchProfilesByIds(rows.map(r => r.customer_id));
  const jobIds = [...new Set(rows.map(r => r.job_request_id).filter(Boolean))];
  let jobMap = {};
  if (jobIds.length) {
    const { data: jobs } = await supabase.from("job_requests").select("id, description, location").in("id", jobIds);
    (jobs || []).forEach(j => { jobMap[j.id] = j; });
  }
  return rows.map(r => {
    const profile = profileMap[r.customer_id];
    const name = profile?.full_name || "Customer";
    return {
      id: r.id,
      rating: r.rating || 0,
      categoryRatings: r.category_ratings || {},
      text: r.review_text || "",
      photos: r.photos || [],
      proReply: r.pro_reply || null,
      proReplyAt: r.pro_reply_at || null,
      moderationStatus: r.moderation_status || "pending",
      createdAt: r.created_at,
      customer: {
        id: r.customer_id, name,
        initials: name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase(),
        avatar_url: profile?.avatar_url || null,
      },
      service: jobMap[r.job_request_id]?.description || "Service",
      location: jobMap[r.job_request_id]?.location || "",
      verified: !!r.job_request_id,
    };
  });
}

async function submitProReply(reviewId, text) {
  const { error } = await supabase.from("reviews").update({ pro_reply: text, pro_reply_at: new Date().toISOString() }).eq("id", reviewId);
  if (error) throw error;
}

async function fetchMessages(userId) {
  const { data: convs, error } = await supabase
    .from("conversations")
    .select("id, customer_id, professional_id, updated_at")
    .or(`customer_id.eq.${userId},professional_id.eq.${userId}`)
    .order("updated_at", { ascending: false })
    .limit(5);
  if (error) throw error;
  if (!convs || !convs.length) return [];

  const otherIds = convs.map(c => c.customer_id === userId ? c.professional_id : c.customer_id);
  const profileMap = await fetchProfilesByIds(otherIds);

  const results = [];
  for (const conv of convs) {
    const otherId = conv.customer_id === userId ? conv.professional_id : conv.customer_id;
    const { data: lastMsgs } = await supabase.from("chat_messages").select("content, created_at, sender_id, type").eq("conversation_id", conv.id).order("created_at", { ascending: false }).limit(1);
    const { data: allMsgs } = await supabase.from("chat_messages").select("id, status").eq("conversation_id", conv.id).neq("sender_id", userId);
    const unread = (allMsgs || []).filter(m => m.status !== 'read').length;
    const lastMsg = lastMsgs?.[0];
    const profile = profileMap[otherId];
    const name = profile?.full_name || "Customer";
    results.push({
      id: conv.id,
      customer: { id: otherId, name, initials: name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase(), avatar_url: profile?.avatar_url || null },
      lastMessage: lastMsg?.type === "image" ? "📷 Photo" : lastMsg?.content || "No messages yet",
      unread, time: timeAgo(lastMsg?.created_at),
    });
  }
  return results;
}

async function fetchNotifications(userId) {
  const { data, error } = await supabase.from("notifications").select("id, type, title, body, created_at, read").eq("user_id", userId).order("created_at", { ascending: false }).limit(20);
  if (error) throw error;
  return (data || []).map(n => ({ id: n.id, type: n.type || "info", title: n.title, body: n.body, time: timeAgo(n.created_at), read: n.read || false }));
}

async function markNotificationsRead(userId) {
  await supabase.from("notifications").update({ read: true }).eq("user_id", userId).eq("read", false);
}

/* ═══════════════════════════════════════════════════════════
   ATOMS
═══════════════════════════════════════════════════════════ */
function Ava({ name, initials, avatar_url, size = 40 }) {
  const [imgErr, setImgErr] = useState(false);
  const color = avaColor(name || initials || "");
  if (avatar_url && !imgErr) {
    return (
      <div style={{ width: size, height: size, borderRadius: "50%", flexShrink: 0, boxShadow: `0 0 0 2px #fff, 0 2px 8px ${color}33`, overflow: "hidden" }}>
        <img src={avatar_url} alt={name || ""} style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={() => setImgErr(true)} />
      </div>
    );
  }
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", flexShrink: 0, background: `linear-gradient(145deg, ${color}EE 0%, ${color}88 100%)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: Math.round(size * 0.32), fontWeight: 700, color: "#fff", fontFamily: T.sans, boxShadow: `0 0 0 2px #fff, 0 2px 8px ${color}44` }}>
      {(initials || "?").slice(0, 2).toUpperCase()}
    </div>
  );
}

function StarRow({ rating, size = 15 }) {
  return (
    <span style={{ display: "inline-flex", gap: 2 }}>
      {[1, 2, 3, 4, 5].map(s => (
        <svg key={s} width={size} height={size} viewBox="0 0 20 20" fill={s <= Math.round(rating) ? T.gold : T.border}>
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </span>
  );
}

function Skel({ w = "100%", h = 16, r = 8, style: s = {} }) {
  return <div style={{ width: w, height: h, borderRadius: r, background: "linear-gradient(90deg, #F0F2F5 25%, #E8EAED 50%, #F0F2F5 75%)", backgroundSize: "200% 100%", animation: "skelShimmer 1.4s infinite", ...s }} />;
}

/* Solid-color icon-chip stat card — same pattern as the four RATING /
   JOBS DONE / SUCCESS / RESPONSE cards on the pro dashboard: a filled
   colored square, a bold sans number, an uppercase caption. */
function StatCard({ icon, bg, value, label }) {
  return (
    <div style={{ background: T.card, borderRadius: T.r16, border: `1px solid ${T.border}`, boxShadow: T.s1, padding: "16px 18px", display: "flex", alignItems: "center", gap: 14 }}>
      <div style={{ width: 44, height: 44, borderRadius: 12, background: bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        {icon}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: T.text, fontFamily: T.sans, lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.faint, fontFamily: T.sans, marginTop: 5 }}>{label}</div>
      </div>
    </div>
  );
}

/* Status pill — mirrors the amber "Needs work" / green "Great" pills
   in the dashboard's "How clients see you" performance snapshot card. */
function StatusPill({ ok }) {
  if (ok === null) {
    return <span style={{ fontSize: 11, fontWeight: 700, color: T.faint, background: T.card2, border: `1px solid ${T.border}`, borderRadius: T.rFull, padding: "3px 10px", fontFamily: T.sans, flexShrink: 0 }}>No data</span>;
  }
  return ok
    ? <span style={{ fontSize: 11, fontWeight: 700, color: T.greenDark, background: T.greenTint, border: `1px solid ${T.greenBorder}`, borderRadius: T.rFull, padding: "3px 10px", fontFamily: T.sans, flexShrink: 0 }}>Great</span>
    : <span style={{ fontSize: 11, fontWeight: 700, color: T.amber, background: T.amberTint, border: `1px solid ${T.amberBd}`, borderRadius: T.rFull, padding: "3px 10px", fontFamily: T.sans, flexShrink: 0 }}>Needs work</span>;
}

/* Reputation snapshot — same shape as the dashboard's "How clients see
   you" card: green eyebrow, italic serif heading, a short list of rows
   each paired with a status pill. */
function ReputationSnapshot({ reviews, pro }) {
  const total = reviews.length;
  const replied = reviews.filter(r => r.proReply).length;
  const verified = reviews.filter(r => r.verified).length;
  const replyPct = total ? Math.round((replied / total) * 100) : null;
  const verifiedPct = total ? Math.round((verified / total) * 100) : null;

  const rows = [
    { label: "Average rating", value: total ? (pro.rating || 0).toFixed(1) : "—", ok: total ? (pro.rating || 0) >= 4.5 : null },
    { label: "Reply rate", value: replyPct === null ? "—" : `${replyPct}%`, ok: replyPct === null ? null : replyPct >= 80 },
    { label: "Verified bookings", value: verifiedPct === null ? "—" : `${verifiedPct}%`, ok: verifiedPct === null ? null : verifiedPct >= 70 },
  ];

  return (
    <div style={{ padding: "18px 18px 4px", borderRadius: 14, border: `1px solid ${T.border}`, background: T.white }}>
      <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: T.green, fontFamily: T.sans, margin: "0 0 4px" }}>How clients see you</p>
      <div style={{ fontFamily: T.serif, fontStyle: "italic", fontSize: 19, fontWeight: 400, color: T.text, letterSpacing: "-0.01em", marginBottom: 14 }}>Reputation snapshot</div>
      <div>
        {rows.map((r, i) => (
          <div key={r.label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "10px 0", borderTop: i > 0 ? `1px solid ${T.borderFaint}` : "none" }}>
            <div>
              <div style={{ fontSize: 12.5, color: T.textMid, fontFamily: T.sans, fontWeight: 500 }}>{r.label}</div>
              <div style={{ fontSize: 11, color: T.faint, fontFamily: T.sans, marginTop: 1 }}>{r.value}</div>
            </div>
            <StatusPill ok={r.ok} />
          </div>
        ))}
      </div>
    </div>
  );
}

function ListSkeleton({ count = 3 }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} style={{ background: T.card, borderRadius: T.r16, border: `1px solid ${T.border}`, padding: "18px 20px", boxShadow: T.s1 }}>
          <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
            <Skel w={40} h={40} r={20} />
            <div style={{ flex: 1 }}><Skel w="40%" h={13} r={4} style={{ marginBottom: 8 }} /><Skel w="25%" h={11} r={4} /></div>
          </div>
          <Skel w="90%" h={12} r={4} style={{ marginBottom: 6 }} />
          <Skel w="70%" h={12} r={4} />
        </div>
      ))}
    </div>
  );
}

function ErrorCard({ message, onRetry }) {
  return (
    <div style={{ background: T.redTint, borderRadius: T.r14, border: `1px solid ${T.redBd}`, padding: 20, display: "flex", alignItems: "flex-start", gap: 12 }}>
      <AlertTriangle size={18} color={T.red} strokeWidth={1.75} style={{ flexShrink: 0, marginTop: 1 }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: T.red, fontFamily: T.sans, marginBottom: 4 }}>Failed to load</div>
        <div style={{ fontSize: 12, color: T.red, fontFamily: T.sans, opacity: 0.8, marginBottom: 10 }}>{message}</div>
        {onRetry && <button onClick={onRetry} style={{ fontSize: 12, fontWeight: 600, color: T.red, background: "none", border: `1px solid ${T.redBd}`, borderRadius: T.r8, padding: "5px 12px", cursor: "pointer", fontFamily: T.sans }}>Try again</button>}
      </div>
    </div>
  );
}

function DemoBanner({ onLogin }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, background: T.amberTint, border: `1px solid ${T.amberBd}`, borderRadius: T.r12, padding: "12px 16px", marginBottom: 24 }}>
      <Info size={16} color={T.amber} strokeWidth={1.75} style={{ flexShrink: 0 }} />
      <span style={{ fontSize: 12.5, color: T.amber, fontFamily: T.sans, flex: 1, lineHeight: 1.5 }}>You're viewing sample data. Sign in to see your real reviews.</span>
      <button onClick={onLogin} style={{ fontSize: 12.5, fontWeight: 700, color: "#fff", background: T.amber, border: "none", borderRadius: T.rFull, padding: "7px 14px", cursor: "pointer", fontFamily: T.sans, flexShrink: 0 }}>Log in</button>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   PREMIUM EMPTY STATE — replaces the old open-book icon and the
   three link buttons with a single seal-style illustration built
   from the brand's own gold and green, plus a rewritten message
   in the interface's voice.
═══════════════════════════════════════════════════════════ */
function PremiumSeal({ size = 64 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <defs>
        <linearGradient id="sealRing" x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor={T.goldLight} />
          <stop offset="100%" stopColor={T.gold} />
        </linearGradient>
        <linearGradient id="sealFill" x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor={T.greenTint} />
          <stop offset="100%" stopColor="#e2f2e9" />
        </linearGradient>
      </defs>
      <circle cx="32" cy="32" r="30" stroke="url(#sealRing)" strokeWidth="1.4" strokeDasharray="1.5 5" strokeLinecap="round" />
      <circle cx="32" cy="32" r="23" fill="url(#sealFill)" stroke={T.greenBorder} strokeWidth="1.4" />
      <path
        d="M32 18.5l3.53 7.16 7.9 1.15-5.72 5.58 1.35 7.87L32 36.6l-7.06 3.66 1.35-7.87-5.72-5.58 7.9-1.15z"
        fill="url(#sealRing)"
      />
    </svg>
  );
}

function ReviewsEmptyState({ navigate }) {
  return (
    <div style={{
      background: T.card, borderRadius: T.r16, border: `1px solid ${T.border}`,
      padding: "52px 24px 46px", textAlign: "center", boxShadow: T.s1,
      display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
    }}>
      <div style={{ marginBottom: 14 }}><PremiumSeal /></div>
      <div style={{ fontFamily: T.serif, fontStyle: "italic", fontSize: 22, fontWeight: 400, color: T.text, letterSpacing: "-0.01em" }}>
        Your reputation starts here
      </div>
      <p style={{ fontSize: 13.5, color: T.sub, fontFamily: T.sans, maxWidth: 340, lineHeight: 1.65, margin: "6px 0 0" }}>
        Finish your first job and the client's rating will land on this page. Strong reviews are what move you higher in search, so this space is worth the wait.
      </p>
    </div>
  );
}

function ReviewsFilteredEmptyState() {
  return (
    <div style={{
      background: T.card, borderRadius: T.r16, border: `1px solid ${T.border}`,
      padding: "40px 24px", textAlign: "center", boxShadow: T.s1,
      display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
    }}>
      <PremiumSeal size={48} />
      <div style={{ fontSize: 15, fontWeight: 700, color: T.text, fontFamily: T.sans, letterSpacing: "-0.01em" }}>No reviews match this filter</div>
      <p style={{ fontSize: 12.5, color: T.sub, fontFamily: T.sans, maxWidth: 300, lineHeight: 1.55 }}>Try a different filter to see more of your reviews.</p>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   HEADER ICON BUTTON + FLYOUTS — shared by the desktop top bar
   and, for the avatar only, the mobile header. Flyouts anchor
   from the top-right instead of the old left-hand sidebar rail.
═══════════════════════════════════════════════════════════ */
const NOTIF_ICON = {
  job_request: { icon: <Wallet size={13} color={T.blue} strokeWidth={1.75} />, bg: T.blueTint, bd: T.blueBd },
  payout:      { icon: <Wallet size={13} color={T.green} strokeWidth={1.75} />, bg: T.greenTint, bd: T.greenBorder },
  review:      { icon: <StarIcon size={13} color={T.gold} strokeWidth={1.75} fill={T.gold} />, bg: T.goldTint, bd: "#eddab0" },
  reminder:    { icon: <Calendar size={13} color={T.amber} strokeWidth={1.75} />, bg: T.amberTint, bd: T.amberBd },
};

/* Tint presets for the circular header icon buttons — same family of
   colored-outline chips used for stat icons on the pro dashboard. */
const ICON_TINTS = {
  blue:  { icon: T.blue,  bg: T.blueTint,  border: T.blueBd },
  amber: { icon: T.amber, bg: T.amberTint, border: T.amberBd },
  green: { icon: T.green, bg: T.greenTint, border: T.greenBorder },
};

function useBtn() {
  const [h, setH] = useState(false);
  const [p, setP] = useState(false);
  const bind = {
    onMouseEnter: () => setH(true),
    onMouseLeave: () => { setH(false); setP(false); },
    onMouseDown:  () => setP(true),
    onMouseUp:    () => setP(false),
  };
  return { h, p, bind };
}

function HeaderIconBtn({ icon, active, onClick, badge, title, tint = "blue" }) {
  const { h, p, bind } = useBtn();
  const c = ICON_TINTS[tint] || ICON_TINTS.blue;
  return (
    <button
      {...bind}
      onClick={onClick}
      title={title}
      style={{
        width: 38, height: 38, borderRadius: "50%",
        border: `1.5px solid ${c.border}`, outline: "none",
        background: c.bg, display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer", transition: T.tx,
        transform: p ? "scale(0.93)" : h || active ? "scale(1.06)" : "scale(1)",
        boxShadow: active ? `0 0 0 3px ${c.bg}, 0 3px 10px ${c.icon}2e` : h ? `0 2px 8px ${c.icon}22` : "none",
        position: "relative", flexShrink: 0,
      }}
    >
      {React.cloneElement(icon, { color: c.icon })}
      {badge > 0 && (
        <span style={{
          position: "absolute", top: -3, right: -3, minWidth: 16, height: 16, padding: "0 3px",
          borderRadius: "50%", background: T.green, border: "2px solid #fff",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 9, fontWeight: 700, color: "#fff", fontFamily: T.sans, lineHeight: 1,
        }}>
          {badge > 9 ? "9+" : badge}
        </span>
      )}
    </button>
  );
}

function NotifFlyout({ notifications, onMarkRead, onViewAll }) {
  const unread = notifications.filter(n => !n.read).length;
  return (
    <div style={{ position: "absolute", right: 0, top: "calc(100% + 10px)", width: 320, background: T.white, borderRadius: T.r16, border: `1px solid ${T.border}`, boxShadow: T.s3, zIndex: 400, overflow: "hidden" }}>
      <div style={{ padding: "14px 18px 10px", borderBottom: `1px solid ${T.borderFaint}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: T.sans }}>Notifications {unread > 0 && <span style={{ color: T.red, fontSize: 12 }}>({unread} new)</span>}</span>
        {unread > 0 && <button onClick={onMarkRead} style={{ fontSize: 12, color: T.green, fontFamily: T.sans, background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>Mark all read</button>}
      </div>
      <div style={{ maxHeight: 300, overflowY: "auto" }}>
        {notifications.length === 0
          ? <div style={{ padding: 24, textAlign: "center", fontSize: 13, color: T.faint, fontFamily: T.sans }}>No notifications</div>
          : notifications.slice(0, 6).map((n, i) => {
            const chip = NOTIF_ICON[n.type];
            return (
            <div key={n.id} style={{ padding: "12px 18px", borderBottom: i < Math.min(5, notifications.length - 1) ? `1px solid ${T.borderFaint}` : "none", background: n.read ? T.white : "#FAFBFE", display: "flex", alignItems: "flex-start", gap: 10 }}>
              <div style={{ width: 28, height: 28, borderRadius: T.r8, background: chip?.bg || T.card2, border: `1px solid ${chip?.bd || T.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
                {chip?.icon || <Bell size={13} color={T.faint} strokeWidth={1.75} />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.text, fontFamily: T.sans, marginBottom: 2 }}>{n.title}</div>
                <div style={{ fontSize: 12, color: T.sub, fontFamily: T.sans, lineHeight: 1.45 }}>{n.body}</div>
                <div style={{ fontSize: 11, color: T.faint, fontFamily: T.sans, marginTop: 3 }}>{n.time}</div>
              </div>
              {!n.read && <span style={{ width: 7, height: 7, borderRadius: "50%", background: T.green, flexShrink: 0, marginTop: 5 }} />}
            </div>
          );})}
      </div>
      <div style={{ padding: "12px 18px", borderTop: `1px solid ${T.borderFaint}` }}>
        <button onClick={onViewAll} style={{ width: "100%", padding: "9px 0", background: T.card2, border: `1px solid ${T.border}`, borderRadius: T.r10, cursor: "pointer", fontSize: 13, fontWeight: 600, color: T.textMid, fontFamily: T.sans }}>View all notifications</button>
      </div>
    </div>
  );
}

function MsgFlyoutRow({ m, onOpen, isLast }) {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={() => onOpen(m.id)} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "12px 18px", background: hov ? T.borderFaint : m.unread ? "#FAFBFE" : T.white, border: "none", borderBottom: !isLast ? `1px solid ${T.borderFaint}` : "none", cursor: "pointer", textAlign: "left", transition: "background 0.12s" }}>
      <Ava name={m.customer.name} initials={m.customer.initials} avatar_url={m.customer.avatar_url} size={36} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: T.text, fontFamily: T.sans }}>{m.customer.name}</span>
          <span style={{ fontSize: 11, color: T.faint, fontFamily: T.sans }}>{m.time}</span>
        </div>
        <div style={{ fontSize: 12, color: T.sub, fontFamily: T.sans, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.lastMessage}</div>
      </div>
      {m.unread > 0 && <span style={{ width: 18, height: 18, background: T.green, borderRadius: "50%", color: "#fff", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{m.unread}</span>}
    </button>
  );
}

function MsgFlyout({ messages, onViewAll, onOpen }) {
  return (
    <div style={{ position: "absolute", right: 0, top: "calc(100% + 10px)", width: 300, background: T.white, borderRadius: T.r16, border: `1px solid ${T.border}`, boxShadow: T.s3, zIndex: 400, overflow: "hidden" }}>
      <div style={{ padding: "14px 18px 10px", borderBottom: `1px solid ${T.borderFaint}` }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: T.sans }}>Messages</span>
      </div>
      {messages.length === 0
        ? <div style={{ padding: 24, textAlign: "center", fontSize: 13, color: T.faint, fontFamily: T.sans }}>No messages yet</div>
        : messages.map((m, i) => <MsgFlyoutRow key={m.id} m={m} onOpen={onOpen} isLast={i === messages.length - 1} />)}
      <div style={{ padding: "10px 18px", borderTop: `1px solid ${T.borderFaint}` }}>
        <button onClick={onViewAll} style={{ width: "100%", padding: "9px 0", background: T.card2, border: `1px solid ${T.border}`, borderRadius: T.r10, cursor: "pointer", fontSize: 13, fontWeight: 600, color: T.textMid, fontFamily: T.sans }}>View all messages</button>
      </div>
    </div>
  );
}

/* Profile dropdown — Bookings removed since it lived only in the old
   sidebar rail; Help & Support stays as the pro's fastest way out of
   a stuck moment. Log Out signs the user out for real. */
function ProfileFlyout({ pro, navigate, onClose, onLogout }) {
  const [hov, setHov] = useState(null);
  const items = [
    { label: "Profile (Pro Dashboard)",   icon: <User size={14} color={T.sub} strokeWidth={1.75} />,      action: () => navigate("/pro/dashboard") },
    { label: "Help & Support",            icon: <HelpCircle size={14} color={T.sub} strokeWidth={1.75} />, action: () => navigate("/help") },
    { label: "Switch to Customer View",   icon: <RefreshCw size={14} color={T.sub} strokeWidth={1.75} />, action: () => navigate("/") },
    { label: "Earnings",                  icon: <Wallet size={14} color={T.sub} strokeWidth={1.75} />,     action: () => navigate("/pro/payouts") },
    { label: "Guides",                    icon: <BookOpen size={14} color={T.sub} strokeWidth={1.75} />,   action: () => navigate("/guides") },
  ];
  return (
    <div style={{ position: "absolute", right: 0, top: "calc(100% + 10px)", width: 232, background: T.white, borderRadius: T.r16, border: `1px solid ${T.border}`, boxShadow: T.s3, zIndex: 400, overflow: "hidden" }}>
      <div style={{ padding: "14px 16px 12px", borderBottom: `1px solid ${T.borderFaint}`, display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ position: "relative", flexShrink: 0 }}>
          <Ava name={pro.name} initials={pro.initials} avatar_url={pro.avatar_url} size={36} />
          <span style={{ position: "absolute", bottom: -1, right: -1, width: 10, height: 10, borderRadius: "50%", background: T.green, border: "2px solid #fff" }} />
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text, fontFamily: T.sans }}>{pro.name}</div>
          <div style={{ fontSize: 11, color: T.faint, fontFamily: T.sans }}>Professional</div>
        </div>
      </div>
      <div style={{ padding: "6px 0" }}>
        {items.map((item, i) => (
          <button key={i} onMouseEnter={() => setHov(i)} onMouseLeave={() => setHov(null)} onClick={() => { item.action(); onClose(); }}
            style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "9px 16px", background: hov === i ? T.borderFaint : "transparent", border: "none", cursor: "pointer", textAlign: "left", fontFamily: T.sans, fontSize: 13, color: T.textMid, transition: "background 0.12s" }}>
            {item.icon} {item.label}
          </button>
        ))}
      </div>
      <div style={{ borderTop: `1px solid ${T.borderFaint}` }}>
        <button onMouseEnter={() => setHov(99)} onMouseLeave={() => setHov(null)} onClick={() => { onLogout(); onClose(); }}
          style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", background: hov === 99 ? T.redTint : "transparent", border: "none", cursor: "pointer", fontFamily: T.sans, fontSize: 13, fontWeight: 600, color: T.red, transition: "background 0.12s" }}>
          <LogOut size={14} color={T.red} strokeWidth={1.75} /> Log Out
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   DESKTOP TOP HEADER — replaces the fixed left icon rail.
   Logo left, messages + notifications + avatar cluster right.
═══════════════════════════════════════════════════════════ */
function TopHeader({ pro, notifications, messages, navigate, onMarkNotifsRead, onLogout, search, onSearchChange }) {
  const [open, setOpen] = useState(null); // 'notif' | 'msg' | 'profile' | null
  const [localNotifs, setLocalNotifs] = useState(notifications);
  const [searchFocused, setSearchFocused] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => setLocalNotifs(notifications), [notifications]);
  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(null); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const unreadN = localNotifs.filter(n => !n.read).length;
  const unreadM = messages.reduce((s, m) => s + (m.unread || 0), 0);

  return (
    <header style={{
      position: "sticky", top: 0, zIndex: 200, background: T.white,
      borderBottom: `1px solid ${T.border}`, height: 60,
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "0 24px",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 20, flexShrink: 0 }}>
        <div onClick={() => navigate("/pro/dashboard")} style={{ cursor: "pointer", display: "flex", alignItems: "center" }}>
          <AppLogo size="sm" />
        </div>
        <button
          onClick={() => navigate("/pro/dashboard")}
          onMouseEnter={e => { e.currentTarget.style.color = T.text; }}
          onMouseLeave={e => { e.currentTarget.style.color = T.sub; }}
          style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", fontFamily: T.sans, fontSize: 13.5, fontWeight: 600, color: T.sub, padding: 0, transition: T.tx }}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
          Back to Dashboard
        </button>
      </div>

      <div style={{ flex: 1, maxWidth: 380, margin: "0 24px", position: "relative" }}>
        <svg style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={searchFocused ? T.green : T.ghost} strokeWidth="2.2"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>
        <input
          value={search}
          onChange={e => onSearchChange(e.target.value)}
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setSearchFocused(false)}
          placeholder="Search reviews…"
          style={{
            width: "100%", boxSizing: "border-box", padding: "9px 14px 9px 38px", borderRadius: T.rFull,
            border: `1.5px solid ${searchFocused ? T.greenBorder : T.border}`,
            background: searchFocused ? T.white : T.card2,
            fontSize: 13, fontFamily: T.sans, color: T.text, outline: "none", transition: T.tx,
          }}
        />
      </div>

      <div ref={wrapRef} style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
        <div style={{ position: "relative" }}>
          <HeaderIconBtn title="Messages" tint="blue" icon={<MessageSquare size={17} strokeWidth={1.75} />} badge={unreadM} active={open === "msg"} onClick={() => setOpen(o => o === "msg" ? null : "msg")} />
          {open === "msg" && <MsgFlyout messages={messages} onViewAll={() => { navigate("/messages"); setOpen(null); }} onOpen={(id) => { navigate(`/messages/${id}`); setOpen(null); }} />}
        </div>

        <div style={{ position: "relative" }}>
          <HeaderIconBtn title="Notifications" tint="amber" icon={<Bell size={17} strokeWidth={1.75} />} badge={unreadN} active={open === "notif"} onClick={() => setOpen(o => o === "notif" ? null : "notif")} />
          {open === "notif" && <NotifFlyout notifications={localNotifs} onMarkRead={() => { setLocalNotifs(n => n.map(x => ({ ...x, read: true }))); onMarkNotifsRead(); }} onViewAll={() => { navigate("/notifications"); setOpen(null); }} />}
        </div>

        <div style={{ position: "relative", marginLeft: 6 }}>
          <div onClick={() => setOpen(o => o === "profile" ? null : "profile")} style={{ cursor: "pointer", borderRadius: "50%", position: "relative", boxShadow: open === "profile" ? `0 0 0 2px ${T.greenBorder}` : "none", transition: T.tx }}>
            <Ava name={pro.name} initials={pro.initials} avatar_url={pro.avatar_url} size={34} />
            <span style={{ position: "absolute", bottom: -1, right: -1, width: 11, height: 11, borderRadius: "50%", background: T.green, border: "2px solid #fff" }} />
          </div>
          {open === "profile" && <ProfileFlyout pro={pro} navigate={navigate} onClose={() => setOpen(null)} onLogout={onLogout} />}
        </div>
      </div>
    </header>
  );
}

/* ═══════════════════════════════════════════════════════════
   MOBILE — borrows Bolt's architecture: a slim top bar carrying
   only identity, and a fixed bottom tab bar carrying navigation,
   rather than stacking every control into one header.
═══════════════════════════════════════════════════════════ */
function MobileHeader({ pro, navigate, onLogout }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <header style={{
      position: "sticky", top: 0, zIndex: 200, background: T.white,
      borderBottom: `1px solid ${T.border}`, height: 52,
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "0 16px",
    }}>
      <div style={{ transform: "scale(0.85)", transformOrigin: "left center" }}><AppLogo size="sm" /></div>
      <div ref={wrapRef} style={{ position: "relative" }}>
        <div onClick={() => setOpen(o => !o)} style={{ cursor: "pointer", position: "relative" }}>
          <Ava name={pro.name} initials={pro.initials} avatar_url={pro.avatar_url} size={30} />
          <span style={{ position: "absolute", bottom: -1, right: -1, width: 10, height: 10, borderRadius: "50%", background: T.green, border: "2px solid #fff" }} />
        </div>
        {open && <ProfileFlyout pro={pro} navigate={navigate} onClose={() => setOpen(false)} onLogout={onLogout} />}
      </div>
    </header>
  );
}

function BottomTab({ icon, label, active, badge, onClick }) {
  return (
    <button onClick={onClick} style={{
      flex: 1, background: "none", border: "none", cursor: "pointer",
      display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
      padding: "8px 0 6px", color: active ? T.green : T.ghost, position: "relative",
    }}>
      <span style={{ position: "relative" }}>
        {icon}
        {badge > 0 && <span style={{ position: "absolute", top: -2, right: -4, width: 7, height: 7, borderRadius: "50%", background: T.green, border: "1.5px solid #fff" }} />}
      </span>
      <span style={{ fontSize: 10.5, fontWeight: active ? 700 : 500, fontFamily: T.sans }}>{label}</span>
    </button>
  );
}

function MobileBottomNav({ navigate, unreadM, unreadN }) {
  return (
    <nav style={{
      position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 200,
      background: T.white, borderTop: `1px solid ${T.border}`,
      display: "flex", alignItems: "stretch",
      paddingBottom: "env(safe-area-inset-bottom, 0px)",
      boxShadow: "0 -4px 16px rgba(13,46,26,0.05)",
    }}>
      <BottomTab icon={<HomeIcon size={19} strokeWidth={1.6} />} label="Home" onClick={() => navigate("/")} />
      <BottomTab icon={<CalendarDays size={19} strokeWidth={1.6} />} label="Bookings" onClick={() => navigate("/bookings")} />
      <BottomTab icon={<MessageSquare size={19} strokeWidth={1.6} />} label="Messages" badge={unreadM} onClick={() => navigate("/messages")} />
      <BottomTab icon={<Bell size={19} strokeWidth={1.6} />} label="Alerts" badge={unreadN} onClick={() => navigate("/notifications")} />
      <BottomTab icon={<StarIcon size={19} strokeWidth={1.6} fill={T.green} />} label="Reviews" active onClick={() => {}} />
    </nav>
  );
}

/* ═══════════════════════════════════════════════════════════
   RIGHT PANEL — rebuilt for the pro, not the customer. Every
   card here answers a question a working pro actually has:
   how do I get booked more, how do payouts work, how do reviews
   affect ranking. Image slots are named below in the code so
   they are easy to find and drop in.
═══════════════════════════════════════════════════════════ */
const EDITORIAL_CARDS = [
  {
    // /public/images/pro/win-more-bookings.png — a pro mid-job, phone
    // visible with a new booking notification; communicates speed/responsiveness.
    img: "/images/jiranipros/win-more-bookings.png",
    eyebrow: "Get booked faster",
    heading: "What top-rated pros do differently",
    desc: "Fast replies and a complete profile are the two biggest levers for showing up higher in search.",
    link: "/tips",
    linkLabel: "See the top-pro checklist",
  },
  {
    // /public/images/pro/payouts-explained.png — M-Pesa confirmation
    // screen or a pro checking their phone after a job, calm and clear.
    img: "/images/jiranipros/payouts-explained.png",
    eyebrow: "Getting paid",
    heading: "How your payouts work",
    desc: "See exactly when a payment is released to M-Pesa after a client confirms the job is done.",
    link: "/pro/payouts",
    linkLabel: "View payout schedule",
  },
  {
    // /public/images/pro/reviews-and-ranking.png — five-star rating
    // graphic or a satisfied client shaking hands with the pro.
    img: "/images/jiranipros/reviews-and-ranking.png",
    eyebrow: "Reputation",
    heading: "How reviews affect your ranking",
    desc: "Ratings, reply speed, and repeat clients all feed into where you land in search results.",
    link: "/guides",
    linkLabel: "Read the ranking guide",
  },
];

function EditorialCard({ card, navigate }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        borderRadius: 16, overflow: "hidden",
        border: `1.5px solid ${hovered ? T.greenBorder : T.border}`,
        background: T.white, transition: T.tx, cursor: "pointer",
        boxShadow: hovered ? "0 8px 28px rgba(30,146,84,0.10)" : "0 1px 4px rgba(0,0,0,0.04)",
      }}
      onClick={() => navigate(card.link)}
    >
      <div style={{ position: "relative", width: "100%", height: 130, overflow: "hidden", background: "#E5E7EB" }}>
        <img
          src={card.img} alt={card.heading}
          style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.45s cubic-bezier(.4,0,.2,1)", transform: hovered ? "scale(1.04)" : "scale(1)", display: "block" }}
          onError={e => { e.currentTarget.style.display = "none"; }}
        />
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 56, background: "linear-gradient(to top, rgba(255,255,255,0.95) 0%, transparent 100%)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", top: 0, left: 0, bottom: 0, width: 28, background: "linear-gradient(to right, rgba(255,255,255,0.30) 0%, transparent 100%)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", top: 0, right: 0, bottom: 0, width: 28, background: "linear-gradient(to left, rgba(255,255,255,0.30) 0%, transparent 100%)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 20, background: "linear-gradient(to bottom, rgba(255,255,255,0.18) 0%, transparent 100%)", pointerEvents: "none" }} />
      </div>
      <div style={{ padding: "14px 16px 16px" }}>
        <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.09em", textTransform: "uppercase", color: T.green, fontFamily: T.sans, marginBottom: 5 }}>{card.eyebrow}</div>
        <div style={{ fontFamily: T.serif, fontSize: 16, fontWeight: 400, color: T.text, lineHeight: 1.3, letterSpacing: "-0.02em", marginBottom: 6, fontStyle: "italic" }}>{card.heading}</div>
        <p style={{ fontSize: 12.5, color: T.sub, lineHeight: 1.65, margin: "0 0 10px", fontFamily: T.sans }}>{card.desc}</p>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 600, color: T.green, fontFamily: T.sans, transition: T.tx, transform: hovered ? "translateX(2px)" : "translateX(0)" }}>
          {card.linkLabel}
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
        </div>
      </div>
    </div>
  );
}

function QuickLink({ link, navigate }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => navigate(link.path)}
      style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #F3F4F6", cursor: "pointer", transition: T.tx }}
    >
      <span style={{ fontSize: 13, color: hovered ? T.green : T.textMid, fontFamily: T.sans, fontWeight: 400, transition: T.tx }}>{link.label}</span>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={hovered ? T.green : T.ghost} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ transition: T.tx, transform: hovered ? "translateX(2px)" : "none" }}>
        <path d="M9 18l6-6-6-6" />
      </svg>
    </div>
  );
}

const PRO_QUICK_LINKS = [
  { label: "Pro Dashboard",       path: "/pro/dashboard" },
  { label: "Manage availability", path: "/pro/availability" },
  { label: "Payout settings",     path: "/pro/payouts" },
  { label: "Help & Support",      path: "/help" },
  { label: "Community Hub for pros", path: "/community-hub" },
];

function RightPanel({ navigate, reviews, pro }) {
  const { isMobile } = useBreakpoint();
  if (isMobile) return null;
  return (
    <aside style={{ width: 280, flexShrink: 0, padding: "32px 20px 32px 0", display: "flex", flexDirection: "column", gap: 24 }}>
      <ReputationSnapshot reviews={reviews} pro={pro} />
      <div>
        <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: T.faint, fontFamily: T.sans, margin: "0 0 10px" }}>For JiraniPros</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {EDITORIAL_CARDS.map((card, i) => <EditorialCard key={i} card={card} navigate={navigate} />)}
        </div>
      </div>
      <div style={{ padding: "18px 16px", borderRadius: 14, border: `1px solid ${T.border}`, background: T.white }}>
        <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: T.faint, fontFamily: T.sans, margin: "0 0 12px" }}>Quick links</p>
        {PRO_QUICK_LINKS.map((link, i) => <QuickLink key={i} link={link} navigate={navigate} />)}
      </div>
    </aside>
  );
}

/* ═══════════════════════════════════════════════════════════
   REVIEW CARD — real data, inline reply composer
═══════════════════════════════════════════════════════════ */
function ReviewCard({ review, onReply, onReport }) {
  const [replying, setReplying] = useState(false);
  const [draft, setDraft] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const catEntries = Object.entries(review.categoryRatings || {}).filter(([, v]) => v > 0);

  const handleSubmit = async () => {
    if (!draft.trim()) return;
    setSubmitting(true);
    try { await onReply(review.id, draft.trim()); setReplying(false); setDraft(""); }
    finally { setSubmitting(false); }
  };

  return (
    <div style={{ background: T.card, borderRadius: T.r16, border: `1px solid ${T.border}`, boxShadow: T.s1, padding: "20px 22px" }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 10, flexWrap: "wrap" }}>
        <Ava name={review.customer.name} initials={review.customer.initials} avatar_url={review.customer.avatar_url} size={40} />
        <div style={{ flex: 1, minWidth: 160 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: T.sans }}>{review.customer.name}</span>
            {review.verified && (
              <span style={{ fontSize: 10.5, fontWeight: 700, color: T.green, background: T.greenTint, border: `1px solid ${T.greenBorder}`, borderRadius: T.rFull, padding: "2px 9px" }}>
                ✓ Verified booking
              </span>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
            <StarRow rating={review.rating} size={13} />
            <span style={{ fontSize: 12, color: T.faint, fontFamily: T.sans }}>· {timeAgo(review.createdAt)}</span>
          </div>
        </div>
      </div>

      {(review.service || review.location) && (
        <div style={{ fontSize: 12.5, color: T.sub, fontFamily: T.sans, marginBottom: 10 }}>
          {review.service}{review.location ? ` · ${review.location}` : ""}
        </div>
      )}

      {review.text && <p style={{ fontSize: 13.5, color: T.textMid, lineHeight: 1.65, margin: "0 0 12px", fontFamily: T.sans }}>{review.text}</p>}

      {catEntries.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 18px", marginBottom: 12, padding: "10px 0", borderTop: `1px solid ${T.borderFaint}`, borderBottom: `1px solid ${T.borderFaint}` }}>
          {catEntries.map(([label, val]) => (
            <div key={label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 11.5, color: T.faint, fontFamily: T.sans }}>{label}</span>
              <StarRow rating={val} size={10} />
            </div>
          ))}
        </div>
      )}

      {review.photos.length > 0 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
          {review.photos.map((src, i) => (
            <div key={i} style={{ width: 68, height: 68, borderRadius: T.r10, overflow: "hidden", border: `1px solid ${T.border}` }}>
              <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </div>
          ))}
        </div>
      )}

      {review.proReply && (
        <div style={{ background: T.greenTint, border: `1px solid ${T.greenBorder}`, borderRadius: T.r12, padding: "12px 14px", marginBottom: 12 }}>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: T.greenDark, fontFamily: T.sans, marginBottom: 4 }}>
            Your reply {review.proReplyAt ? `· ${timeAgo(review.proReplyAt)}` : ""}
          </div>
          <div style={{ fontSize: 13, color: T.textMid, fontFamily: T.sans, lineHeight: 1.55 }}>{review.proReply}</div>
        </div>
      )}

      {replying ? (
        <div style={{ marginTop: 4 }}>
          <textarea
            value={draft}
            onChange={e => setDraft(e.target.value)}
            placeholder="Write a public reply to this review…"
            rows={3}
            style={{ width: "100%", padding: "10px 12px", borderRadius: T.r10, border: `1.5px solid ${T.border}`, fontFamily: T.sans, fontSize: 13, color: T.text, outline: "none", resize: "none", boxSizing: "border-box", marginBottom: 8 }}
            onFocus={e => e.target.style.borderColor = T.green}
            onBlur={e => e.target.style.borderColor = T.border}
          />
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={handleSubmit} disabled={submitting || !draft.trim()} style={{ padding: "8px 16px", borderRadius: T.rFull, border: "none", background: T.green, color: "#fff", fontSize: 12.5, fontWeight: 700, cursor: submitting ? "default" : "pointer", fontFamily: T.sans, opacity: submitting || !draft.trim() ? 0.6 : 1 }}>
              {submitting ? "Posting…" : "Post reply"}
            </button>
            <button onClick={() => { setReplying(false); setDraft(""); }} style={{ padding: "8px 16px", borderRadius: T.rFull, border: `1px solid ${T.border}`, background: "none", color: T.sub, fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: T.sans }}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", gap: 16, paddingTop: 2 }}>
          {!review.proReply && (
            <button onClick={() => setReplying(true)} style={{ display: "flex", alignItems: "center", gap: 5, background: "none", border: "none", cursor: "pointer", color: T.green, fontSize: 12.5, fontWeight: 600, fontFamily: T.sans, padding: 0 }}>
              <MessageCircle size={13} strokeWidth={1.75} /> Reply
            </button>
          )}
          <button onClick={() => onReport(review.id)} style={{ display: "flex", alignItems: "center", gap: 5, background: "none", border: "none", cursor: "pointer", color: T.faint, fontSize: 12.5, fontWeight: 500, fontFamily: T.sans, padding: 0 }}>
            <Flag size={12} strokeWidth={1.75} /> Report
          </button>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════════════════ */
export default function ReviewsRatings() {
  const navigate = useNavigate();
  const { open: openLogin } = useLoginModal();
  const { isMobile } = useBreakpoint();
  const { logoutUser } = useAuth();

  const [userId, setUserId] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [isDemo, setIsDemo] = useState(false);
  const [pro, setPro] = useState(null);
  const [proError, setProError] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState({ pro: true, reviews: true, notifications: true, messages: true });
  const [errors, setErrors] = useState({});
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");

  const setLoad = (key, val) => setLoading(p => ({ ...p, [key]: val }));
  const setError = (key, val) => setErrors(p => ({ ...p, [key]: val }));

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user?.id) setUserId(session.user.id);
      setAuthChecked(true);
    }).catch(() => setAuthChecked(true));
    const { data: listener } = supabase.auth.onAuthStateChange((_e, session) => setUserId(session?.user?.id || null));
    return () => listener?.subscription?.unsubscribe();
  }, []);

  const loadAll = useCallback(async (uid) => {
    if (!uid) {
      setIsDemo(true);
      setPro(SEED_PRO);
      setReviews(SEED_REVIEWS);
      setNotifications(SEED_NOTIFICATIONS);
      setMessages(SEED_MESSAGES);
      setLoading({ pro: false, reviews: false, notifications: false, messages: false });
      return;
    }
    setIsDemo(false);
    fetchProProfile(uid).then(p => { setPro(p); setProError(null); setLoad("pro", false); }).catch(e => { setProError(e.message); setLoad("pro", false); });
    fetchReviewsFull(uid).then(d => { setReviews(d); setError("reviews", null); setLoad("reviews", false); }).catch(e => { setError("reviews", e.message); setLoad("reviews", false); });
    fetchNotifications(uid).then(d => { setNotifications(d); setLoad("notifications", false); }).catch(() => setLoad("notifications", false));
    fetchMessages(uid).then(d => { setMessages(d); setLoad("messages", false); }).catch(() => setLoad("messages", false));
  }, []);

  useEffect(() => { if (authChecked) loadAll(userId); }, [authChecked, userId, loadAll]);

  const retryReviews = useCallback(() => {
    if (!userId) return;
    setLoad("reviews", true);
    fetchReviewsFull(userId).then(d => { setReviews(d); setError("reviews", null); setLoad("reviews", false); }).catch(e => { setError("reviews", e.message); setLoad("reviews", false); });
  }, [userId]);

  const handleReply = useCallback(async (reviewId, text) => {
    if (isDemo) { setReviews(p => p.map(r => r.id === reviewId ? { ...r, proReply: text, proReplyAt: new Date().toISOString() } : r)); return; }
    await submitProReply(reviewId, text);
    setReviews(p => p.map(r => r.id === reviewId ? { ...r, proReply: text, proReplyAt: new Date().toISOString() } : r));
  }, [isDemo]);

  const handleReport = useCallback((reviewId) => {
    navigate(`/report/${pro?.id || "unknown"}?reviewId=${reviewId}`);
  }, [navigate, pro]);

  const handleMarkNotifsRead = useCallback(async () => {
    if (isDemo) { setNotifications(n => n.map(x => ({ ...x, read: true }))); return; }
    if (!userId) return;
    try {
      await markNotificationsRead(userId);
      setNotifications(n => n.map(x => ({ ...x, read: true })));
    } catch { /* best-effort — local flyout state already reflects read */ }
  }, [userId, isDemo]);

  const handleLogout = useCallback(async () => {
    if (isDemo) { openLogin(); return; }
    try { await logoutUser?.(); } finally { navigate("/"); }
  }, [isDemo, logoutUser, navigate]);

  const filtered = reviews.filter(r => {
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const hit = (r.customer?.name || "").toLowerCase().includes(q)
        || (r.text || "").toLowerCase().includes(q)
        || (r.service || "").toLowerCase().includes(q);
      if (!hit) return false;
    }
    if (filter === "All") return true;
    if (filter === "5 star") return r.rating === 5;
    if (filter === "4 star") return r.rating === 4;
    if (filter === "3 star") return r.rating === 3;
    if (filter === "2 star") return r.rating === 2;
    if (filter === "1 star") return r.rating === 1;
    if (filter === "With photos") return r.photos.length > 0;
    if (filter === "Needs reply") return !r.proReply;
    return true;
  });

  const distribution = [5, 4, 3, 2, 1].map(star => ({
    star, count: reviews.filter(r => r.rating === star).length,
  }));
  const maxCount = Math.max(1, ...distribution.map(d => d.count));

  const categoryAverages = CATEGORY_LABELS.map(label => {
    const vals = reviews.map(r => r.categoryRatings?.[label]).filter(v => v > 0);
    const avg = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
    return { label, avg, count: vals.length };
  }).filter(c => c.count > 0);

  const needsReplyCount = reviews.filter(r => !r.proReply).length;
  const unreadM = messages.reduce((s, m) => s + (m.unread || 0), 0);
  const unreadN = notifications.filter(n => !n.read).length;

  if (!authChecked || (loading.pro && !pro && !proError)) {
    return (
      <div style={{ background: T.bg, minHeight: "100vh", fontFamily: T.sans }}>
        <div style={{ height: 60, background: T.white, borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", padding: "0 24px" }}>
          <div><AppLogo size="sm" /></div>
        </div>
        <main style={{ maxWidth: 900, margin: "0 auto", padding: "28px 16px 80px" }}>
          <Skel w="40%" h={28} r={6} style={{ marginBottom: 24 }} />
          <ListSkeleton count={3} />
        </main>
      </div>
    );
  }

  if (!pro && proError) {
    return (
      <div style={{ background: T.bg, minHeight: "100vh", fontFamily: T.sans, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24 }}>
        <div style={{ maxWidth: 420, width: "100%", textAlign: "center" }}>
          <AlertTriangle size={26} color={T.red} strokeWidth={1.75} style={{ marginBottom: 12 }} />
          <h1 style={{ fontSize: 19, fontWeight: 700, color: T.text, marginBottom: 8, fontFamily: T.sans }}>Couldn't load your reviews</h1>
          <p style={{ fontSize: 13.5, color: T.sub, marginBottom: 20, fontFamily: T.sans }}>{proError}</p>
          <button onClick={() => loadAll(userId)} style={{ padding: 13, background: T.green, border: "none", borderRadius: T.r12, color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: T.sans, boxShadow: T.sGreen }}>Try again</button>
        </div>
      </div>
    );
  }

  if (!pro) return null;

  return (
    <div style={{ background: T.bg, minHeight: "100vh", fontFamily: T.sans }}>
      <style>{`
        * { box-sizing: border-box; }
        button:focus-visible { outline: 2px solid ${T.green}; outline-offset: 2px; }
        @keyframes skelShimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-thumb { background: #E5E7EB; border-radius: 99px; }
      `}</style>

      {isMobile
        ? <MobileHeader pro={pro} navigate={navigate} onLogout={handleLogout} />
        : <TopHeader pro={pro} notifications={notifications} messages={messages} navigate={navigate} onMarkNotifsRead={handleMarkNotifsRead} onLogout={handleLogout} search={search} onSearchChange={setSearch} />
      }

      <div style={{ display: "flex", justifyContent: "center" }}>
        <div style={{ width: "100%", maxWidth: isMobile ? "100%" : 1040, display: "flex", gap: 0, padding: isMobile ? "0" : "0 24px", alignItems: "flex-start" }}>

          {/* CENTRE COLUMN */}
          <div style={{ flex: 1, minWidth: 0, paddingTop: isMobile ? 20 : 32, paddingBottom: isMobile ? 84 : 60, paddingRight: isMobile ? 0 : 32 }}>
            <main style={{ padding: isMobile ? "0 16px" : 0 }}>
              {isDemo && <DemoBanner onLogin={() => openLogin()} />}

              <section style={{ marginBottom: 26 }}>
                <p style={{ margin: "0 0 8px", fontSize: 11, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: T.green, fontFamily: T.sans }}>
                  {pro.reviewCount || reviews.length} review{(pro.reviewCount || reviews.length) === 1 ? "" : "s"} total
                  {needsReplyCount > 0 ? ` · ${needsReplyCount} awaiting your reply` : ""}
                </p>
                <h1 style={{ margin: "0 0 6px", fontFamily: T.serif, fontSize: isMobile ? 28 : 34, fontWeight: 400, fontStyle: "italic", color: T.text, letterSpacing: "-0.03em", lineHeight: 1.12 }}>
                  Reviews & Ratings
                </h1>
                <p style={{ fontSize: 14, color: T.sub, fontFamily: T.sans, margin: 0, lineHeight: 1.6 }}>
                  What clients are saying about your work.
                </p>
              </section>

              <section style={{ marginBottom: 26 }}>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: 12 }}>
                  <StatCard
                    icon={<StarIcon size={19} color="#fff" fill="#fff" strokeWidth={1.5} />}
                    bg={T.gold}
                    value={reviews.length ? (pro.rating || 0).toFixed(1) : "—"}
                    label="Rating"
                  />
                  <StatCard
                    icon={<MessageCircle size={19} color="#fff" strokeWidth={1.75} />}
                    bg={T.green}
                    value={pro.reviewCount || reviews.length || 0}
                    label="Reviews"
                  />
                  <StatCard
                    icon={<RefreshCw size={19} color="#fff" strokeWidth={1.75} />}
                    bg={T.blue}
                    value={reviews.length ? `${Math.round((reviews.filter(r => r.proReply).length / reviews.length) * 100)}%` : "—"}
                    label="Reply rate"
                  />
                  <StatCard
                    icon={<AlertTriangle size={19} color="#fff" strokeWidth={1.75} />}
                    bg={T.purple}
                    value={reviews.length ? needsReplyCount : "—"}
                    label="Awaiting reply"
                  />
                </div>
              </section>

              {/* SUMMARY */}
              {reviews.length > 0 && (
                <section style={{ marginBottom: 28 }}>
                  <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14 }}>
                    <div style={{ background: T.card, borderRadius: T.r16, border: `1px solid ${T.border}`, boxShadow: T.s2, padding: "22px 22px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 18 }}>
                        <div style={{ fontFamily: T.serif, fontStyle: "italic", fontSize: 44, fontWeight: 400, color: T.text, letterSpacing: "-0.03em", lineHeight: 1 }}>
                          {(pro.rating || 0).toFixed(1)}
                        </div>
                        <div>
                          <StarRow rating={pro.rating || 0} size={16} />
                          <div style={{ fontSize: 12, color: T.sub, fontFamily: T.sans, marginTop: 5 }}>{pro.reviewCount || reviews.length} reviews</div>
                        </div>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {distribution.map(d => (
                          <div key={d.star} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <span style={{ fontSize: 11.5, color: T.faint, fontFamily: T.sans, width: 32 }}>{d.star} ★</span>
                            <div style={{ flex: 1, height: 8, borderRadius: T.rFull, background: T.borderFaint, overflow: "hidden" }}>
                              <div style={{ height: "100%", width: `${(d.count / maxCount) * 100}%`, background: `linear-gradient(90deg, ${T.greenLight}, ${T.green})`, borderRadius: T.rFull, transition: "width 0.5s ease" }} />
                            </div>
                            <span style={{ fontSize: 11.5, color: T.faint, fontFamily: T.sans, width: 18, textAlign: "right" }}>{d.count}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div style={{ background: T.card, borderRadius: T.r16, border: `1px solid ${T.border}`, boxShadow: T.s2, padding: "22px 22px" }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: T.text, fontFamily: T.sans, marginBottom: 16 }}>Category breakdown</div>
                      {categoryAverages.length === 0 ? (
                        <div style={{ fontSize: 12.5, color: T.faint, fontFamily: T.sans }}>No category ratings yet.</div>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                          {categoryAverages.map(c => (
                            <div key={c.label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                              <span style={{ fontSize: 12.5, color: T.textMid, fontFamily: T.sans, fontWeight: 500 }}>{c.label}</span>
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <StarRow rating={c.avg} size={12} />
                                <span style={{ fontSize: 12.5, fontWeight: 700, color: T.text, fontFamily: T.sans, width: 26, textAlign: "right" }}>{c.avg.toFixed(1)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </section>
              )}

              {/* FILTERS */}
              {reviews.length > 0 && (
                <section style={{ marginBottom: 18 }}>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {FILTERS.map(f => {
                      const isActive = filter === f;
                      const isStar = f.includes("star");
                      return (
                        <button key={f} onClick={() => setFilter(f)} style={{
                          display: "flex", alignItems: "center", gap: 6,
                          padding: "7px 15px 7px 12px", borderRadius: T.rFull,
                          border: `1.5px solid ${isActive ? T.text : T.border}`,
                          background: isActive ? T.text : T.card, color: isActive ? "#fff" : T.sub,
                          fontSize: 12.5, fontWeight: isActive ? 700 : 500, cursor: "pointer", fontFamily: T.sans, transition: T.tx,
                          boxShadow: isActive ? "0 2px 8px rgba(15,17,23,0.18)" : "none",
                        }}>
                          {isStar
                            ? <StarIcon size={12} strokeWidth={1.5} fill={T.gold} color={T.gold} />
                            : f === "All"
                            ? <HomeIcon size={13} strokeWidth={2} color={isActive ? "#fff" : T.ghost} />
                            : f === "With photos"
                            ? <Camera size={13} strokeWidth={2} color={isActive ? "#fff" : T.ghost} />
                            : <MessageCircle size={13} strokeWidth={2} color={isActive ? "#fff" : T.ghost} />}
                          {f}
                        </button>
                      );
                    })}
                  </div>
                </section>
              )}

              {/* LIST */}
              <section>
                {loading.reviews ? <ListSkeleton count={3} />
                  : errors.reviews ? <ErrorCard message={errors.reviews} onRetry={retryReviews} />
                  : reviews.length === 0 ? <ReviewsEmptyState navigate={navigate} />
                  : filtered.length === 0 ? <ReviewsFilteredEmptyState />
                  : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                      {filtered.map(r => <ReviewCard key={r.id} review={r} onReply={handleReply} onReport={handleReport} />)}
                    </div>
                  )}
              </section>
            </main>
          </div>

          {/* RIGHT PANEL */}
          {!isMobile && (
            <div style={{ paddingTop: 32, flexShrink: 0, width: 280 }}>
              <RightPanel navigate={navigate} reviews={reviews} pro={pro} />
            </div>
          )}
        </div>
      </div>

      {isMobile && <MobileBottomNav navigate={navigate} unreadM={unreadM} unreadN={unreadN} />}
    </div>
  );
}